import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Add a note to a post
 * @param {string} postId - The ID of the post
 * @param {string} userId - The ID of the user creating the note
 * @param {string} userEmail - The email of the user creating the note
 * @param {string} content - The note content
 * @param {Array} taggedUsers - Array of user emails that are tagged
 * @returns {Promise<string>} The ID of the created note
 */
export const addNoteToPost = async (postId, userId, userEmail, content, taggedUsers = []) => {
  try {
    const notesRef = collection(db, 'socialMediaPosts', postId, 'notes');
    const noteData = {
      content,
      userId,
      userEmail,
      taggedUsers,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(notesRef, noteData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding note:', error);
    throw error;
  }
};

/**
 * Get all notes for a post
 * @param {string} postId - The ID of the post
 * @returns {Promise<Array>} Array of notes
 */
export const getPostNotes = async (postId) => {
  try {
    const notesRef = collection(db, 'socialMediaPosts', postId, 'notes');
    const q = query(notesRef, orderBy('createdAt', 'asc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting post notes:', error);
    throw error;
  }
};

/**
 * Update a note
 * @param {string} postId - The ID of the post
 * @param {string} noteId - The ID of the note
 * @param {string} content - Updated content
 * @param {Array} taggedUsers - Updated tagged users
 * @returns {Promise<void>}
 */
export const updateNote = async (postId, noteId, content, taggedUsers = []) => {
  try {
    const noteRef = doc(db, 'socialMediaPosts', postId, 'notes', noteId);
    await updateDoc(noteRef, {
      content,
      taggedUsers,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

/**
 * Delete a note
 * @param {string} postId - The ID of the post
 * @param {string} noteId - The ID of the note
 * @returns {Promise<void>}
 */
export const deleteNote = async (postId, noteId) => {
  try {
    const { deleteDoc } = await import('firebase/firestore');
    const noteRef = doc(db, 'socialMediaPosts', postId, 'notes', noteId);
    await deleteDoc(noteRef);
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

/**
 * Get all users in the system (for tagging)
 * @returns {Promise<Array>} Array of users
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Get notifications for a user
 * @param {string} userEmail - The email of the user
 * @returns {Promise<Array>} Array of notifications
 */
export const getUserNotifications = async (userEmail) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef, 
      where('targetUser', '==', userEmail)
      // Removed orderBy to avoid index requirement - will sort in JavaScript
    );
    const querySnapshot = await getDocs(q);
    
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript (descending - newest first)
    notifications.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return bTime - aTime;
    });
    
    return notifications;
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

/**
 * Create a notification for tagged users
 * @param {string} postId - The ID of the post
 * @param {string} fromUser - The email of the user who created the note
 * @param {Array} taggedUsers - Array of user emails to notify
 * @param {string} noteContent - The note content
 * @returns {Promise<void>}
 */
export const createNotifications = async (postId, fromUser, taggedUsers, noteContent) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    
    const notificationPromises = taggedUsers.map(userEmail => 
      addDoc(notificationsRef, {
        type: 'note_tag',
        postId,
        fromUser,
        targetUser: userEmail,
        content: `You were mentioned in a note on a ${postId ? 'post' : 'comment'}`,
        noteContent: noteContent.substring(0, 100) + (noteContent.length > 100 ? '...' : ''),
        isRead: false,
        createdAt: serverTimestamp()
      })
    );
    
    await Promise.all(notificationPromises);
  } catch (error) {
    console.error('Error creating notifications:', error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The ID of the notification
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};
